import { Injectable } from "@angular/core";
import type { IApiResponse, Endpoint, PrimaryKeyId } from "src/app/models";

import { HTTP_METHOD } from ".";
import { HttpService } from "../http.service";
import { UserService } from "../user.service";

@Injectable({
  providedIn: "root",
})
export class ReportTemplatesApiService {
  private _tenantReportTemplates = "TenantReportTemplates" as Endpoint;

  constructor(private http: HttpService, private us: UserService) {}

  /**
   * Gets all Tenant Report Templates
   * @returns {Promise<IApiResponse<IHttpUser>>} TBC
   * @since 2.8.0
   */
  async getTemplates(): Promise<IApiResponse<_HttpGetTemplates[]>> {
    const { tenantId } = await this.us.checkAndGetUserIds();
    return this.http.request<_HttpGetTemplates[]>(
      HTTP_METHOD.GET,
      this._tenantReportTemplates,
      {
        tenantId: tenantId,
      },
      null,
      true
    );
  }

  /**
   * Gets a single Tenant Report Template
   *
   * @returns {Promise<IApiResponse<any>>} TBC
   * @since 2.8.0
   */
  async getTemplate(
    templateId: number
  ): Promise<IApiResponse<_HttpGetTemplate>> {
    const { tenantId } = await this.us.checkAndGetUserIds();
    return this.http.request<_HttpGetTemplate>(
      HTTP_METHOD.GET,
      this._tenantReportTemplates,
      {
        tenantId: tenantId,
        templateId: templateId,
      },
      null,
      true
    );
  }

  /**
   * Creates a new report template
   * @returns {Promise<IApiResponse<any>>} TBC
   * @since 2.8.0
   */
  async create(
    templateName: string,
    items: any[]
  ): Promise<IApiResponse<{ templateId: number }>> {
    const { tenantId } = await this.us.checkAndGetUserIds();
    return this.http.request<{ templateId: number }>(
      HTTP_METHOD.POST,
      this._tenantReportTemplates,
      {},
      {
        tenantId: tenantId,
        templateName: templateName,
        items,
      },
      true
    );
  }

  /**
   * Edit a report template
   *
   * @param templateId
   * @param templateName
   * @param items - optional, leave as undefined to only update the name
   * @returns {Promise<IApiResponse<any>>} TBC
   * @since 2.8.0
   */
  async edit(
    templateId: number,
    templateName: string,
    items?: any[]
  ): Promise<IApiResponse<any>> {
    return this.http.request(
      HTTP_METHOD.PUT,
      this._tenantReportTemplates,
      {},
      {
        templateId: templateId,
        templateName: templateName,
        items,
      },
      true
    );
  }

  /**
   * Deletes a report from the server
   * @param reportId
   * @returns
   */
  async delete(templateId: number): Promise<IApiResponse> {
    const { tenantId } = await this.us.checkAndGetUserIds();
    return this.http.request(
      HTTP_METHOD.DELETE,
      this._tenantReportTemplates,
      {},
      {
        tenantId,
        templateId,
      },
      true
    );
  }
}

type _HttpGetTemplates = {
  readonly templateId: PrimaryKeyId;
  readonly templateName: string;
  readonly lastModified: number;
  readonly preview: any;
  readonly storageToken: string;
};

type _HttpGetTemplate = {
  readonly templateId: PrimaryKeyId;
  readonly templateName: string;
  readonly lastModified: number;
  readonly items: [];
};
